<template>
  <div>
    <SDialog
      v-if="showOptedIn"
      :show-dialog="showOptedIn"
      :width="360"
      :title="$t('header.linkingSuccess')"
      :text="`${$t('rewards.header.enrolledSuccess')} ${$t('rewards.p.enrolledSuccess')}`"
      @close="showOptedIn = false"
    >
      <VRow no-gutters>
        <VCol><VHtml :html="$t('rewards.dialog.p.seeRewards')" /></VCol>
      </VRow>
      <VCardActions class="text-center mt-4">
        <VRow no-gutters><VCol cols="12"><SBtn action block color="primary" @click="showOptedIn = false">{{ $t('input.btn.close') }}</SBtn></VCol></VRow>
      </VCardActions>
    </SDialog>

    <SDialog v-if="showOptedOut" :show-dialog="showOptedOut" :width="360" @close="showOptedOut = false">
      <VRow no-gutters><VCol><VHtml :html="$t('rewards.dialog.p.optOutSuccess')" /></VCol></VRow>
      <VCardActions class="text-center mt-4">
        <VRow no-gutters><VCol cols="12"><SBtn action block color="primary" @click="showOptedOut = false">{{ $t('input.btn.close') }}</SBtn></VCol></VRow>
      </VCardActions>
    </SDialog>

    <VDialog
      v-if="!isDemoContext"
      :model-value="showRewardsOptIn"
      persistent
      :width="325"
      style="z-index:2147483640;"
    >
      <VCard class="pa-2">
        <SLoadingOverlay :loading="loading" />
        <VCardTitle v-if="i18n.has('rewards.dialog.title.signUp')" class="text-center">
          <h3><VHtml :html="$t('rewards.dialog.title.signUp')" /></h3>
        </VCardTitle>
        <VCardText class="text-center">
          <VRow class="v2-gutters"><VCol><VHtml :html="$t('rewards.dialog.p.signUp')" /></VCol></VRow>
          <VRow class="v2-gutters"><VCol><VHtml :html="$t('rewards.dialog.p.termsAndConditions')" /></VCol></VRow>
        </VCardText>
        <VCardActions class="text-center">
          <VRow class="v2-gutters">
            <VCol cols="12"><SBtn action block color="primary" @click="optIn">{{ $t('rewards.dialog.btn.signUp') }}</SBtn></VCol>
            <VCol><SBtn variant="text" class="text-underline" data-testid="rewards-maybe-later" @click="optOut">{{ $t('rewards.dialog.btn.optOut') }}</SBtn></VCol>
          </VRow>
        </VCardActions>
        <VCardText class="text-center mt-4"><VHtml :html="$t('rewards.dialog.p.optOut')" /></VCardText>
      </VCard>
    </VDialog>

    <VDialog v-else :model-value="showRewardsOptIn" persistent :width="325" style="z-index:2147483640;">
      <SLoadingOverlay :loading="loading" />
      <VCard class="pa-2">
        <VCardTitle class="text-center"><h3>{{ $t('rewards.dialog.title.demonstratorUpdate') }}</h3></VCardTitle>
        <VCardText class="text-center">
          <VRow class="v2-gutters"><VCol>{{ $t('rewards.dialog.p.demonstratorUpdate') }}</VCol></VRow>
          <VRow class="v2-gutters"><VCol><VHtml :html="$t('rewards.dialog.p.demonstratorLearnMore')" /></VCol></VRow>
        </VCardText>
        <VCardActions class="text-center">
          <VRow class="v2-gutters"><VCol cols="12"><SBtn action block color="primary" data-testid="demo-accept-and-close" @click="optIn">{{ $t('input.btn.close') }}</SBtn></VCol></VRow>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>

<script setup lang="ts">
import VHtml from '~/components/base/VHtml.vue'
import { UserRole } from '~/types/account'

import { COMMUNITY_JOIN_STAMPIN_UP } from '~/utils/communityRoutes'
import { JOIN_ROOT } from '~/utils/joinRoutes'
import { stripSubLocalePrefix } from '~/utils/locale'
const i18n = useI18n()
const { processAction, loading } = useAction()
const authStore = useAuthStore()
const rootStore = useRootStore()
const { user, userRole, isAuthenticated, isAgentOrImpersonating } = useSAuthState()
const auth = useSAuth()
const route = useRoute()
const loyaltyStatus = computed(() => user.value?.loyaltyOptIn)
const showOptedIn = ref(false)
const showOptedOut = ref(false)

// JOIN_ROOT covers the whole join flow: the /join/* steps use the join layout, which doesn't mount this dialog.
const blacklistRoutes: string[] = [COMMUNITY_JOIN_STAMPIN_UP, JOIN_ROOT]
const isBlacklistedRoute = computed(() => blacklistRoutes.includes(stripSubLocalePrefix(route.path, route.params.lang as string | undefined)))
const rewardsUserTypes = computed(() => isAuthenticated.value && [UserRole.Customer, UserRole.Demonstrator].includes(userRole.value) && !authStore.isEmployee)
// The opt-in dialog is open from the first render for a signed-in user who has
// not answered yet, and this component hydrates inside the page's Suspense.
// Vue holds its mounted hooks until that Suspense resolves, but VOverlay's
// scroll-lock runs on a setTimeout from setup regardless -- it finds no overlay
// root and throws (`reading 'classList'`), which Nuxt turns into a 500 on every
// page for that user. Opening only after mount avoids the race; same guard as
// InviteProcessing.
const isMounted = ref(false)
onMounted(() => { isMounted.value = true })
// An invitation link is its own dialog flow (InviteProcessing). A customer who
// creates their account there is signed in with no rewards answer yet, and this
// dialog would open underneath the invite's welcome -- two modal dialogs, and
// closing the top one strands focus in a persistent one. Wait until the invite
// flow clears the query.
const inInviteFlow = computed(() => !!route.query.invitation)
const showRewardsOptIn = computed(() => isMounted.value && !inInviteFlow.value && rewardsUserTypes.value && loyaltyStatus.value === undefined && !isAgentOrImpersonating.value && !isBlacklistedRoute.value)
const isDemoContext = computed(() => rootStore.isActiveDemo || userRole.value === UserRole.Demonstrator)

const optIn = () => processAction(async () => {
  await $api()('loyalty/opt-in/?enable=true', { method: 'PATCH' })
  await auth.fetchSession()
  if (!isDemoContext.value) showOptedIn.value = true
})

const optOut = () => processAction(async () => {
  await $api()('loyalty/opt-in/?enable=false', { method: 'PATCH' })
  await auth.fetchSession()
  showOptedOut.value = true
})
</script>
